import React from 'react';
import { cn } from '../../lib/utils';
import { PlayerAvailabilityTimelineData } from '../../hooks/usePlayerDetails';

interface PlayerSeasonTimelineProps {
  seasonData: PlayerAvailabilityTimelineData;
  className?: string;
}

export const PlayerSeasonTimeline: React.FC<PlayerSeasonTimelineProps> = ({
  seasonData,
  className
}) => {
  // Parse season dates (assuming format like "23/24")
  const getSeasonDates = (season: string) => {
    const parts = season.split('/');
    if (parts.length !== 2) return { start: null, end: null };
    
    const startYear = parseInt(`20${parts[0]}`);
    const endYear = parseInt(`20${parts[1]}`);
    
    return {
      start: new Date(`${startYear}-08-01`), // August 1st of start year
      end: new Date(`${endYear}-05-31`)      // May 31st of end year
    };
  };
  
  const { start, end } = getSeasonDates(seasonData.season);
  
  // If we can't parse the season dates, show a simplified view
  if (!start || !end) {
    return (
      <div className={cn("border border-gray-500 bg-transparent hover:bg-[#26222E] rounded-lg p-3", className)}>
        <div className="flex justify-between items-center">
          <div>
            <span className="font-semibold text-[#CECFD2]">{seasonData.season}</span>
            <span className="text-[#CECFD2]/60 mx-2">•</span>
            <span className="text-[#CECFD2]">{seasonData.club.name}</span>
          </div>
          <div>
            <span className="text-sm text-red-500">
              {seasonData.games_missed_injury} games missed
            </span>
            <span className="text-[#CECFD2]/60 mx-2">•</span>
            <span className="text-sm text-[#CECFD2]">
              Bradford Factor: {seasonData.bradford_factor}
            </span>
          </div>
        </div>
      </div>
    );
  }
  
  // Calculate the total duration of the season in milliseconds
  const seasonDuration = end.getTime() - start.getTime();
  
  return (
    <div className={cn("border border-gray-500 bg-transparent hover:bg-[#26222E] rounded-lg p-3 transition-colors", className)}>
      <div className="flex justify-between items-center mb-3">
        <div>
          <span className="font-semibold text-[#CECFD2]">{seasonData.season}</span>
          <span className="text-[#CECFD2]/60 mx-2">•</span>
          <span className="text-[#CECFD2]">{seasonData.club.name}</span>
          <span className="text-[#CECFD2]/60 mx-2">•</span>
          <span className="text-[#CECFD2]">{seasonData.league.name}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm">
            <span className="text-[#CECFD2]">{seasonData.appearances || 0} apps</span>
          </span>
          <span className="text-sm text-red-500">
            {seasonData.games_missed_injury || 0} games missed
          </span>
          <span className="text-[#CECFD2]/60 mx-2">•</span>
          <span className="text-sm text-[#CECFD2]">
            Bradford Factor: {seasonData.bradford_factor}
          </span>
        </div>
      </div>
      
      {/* Season Timeline */}
      <div className="relative h-12 bg-[#26222E] rounded-lg overflow-hidden">
        {/* Month markers - 10 markers for the 10 months from August to May */}
        {Array.from({ length: 10 }).map((_, i) => {
          const monthPosition = (i / 9) * 100;
          return (
            <div 
              key={i} 
              className="absolute top-0 bottom-0 w-px bg-gray-700"
              style={{ left: `${monthPosition}%` }}
            />
          );
        })}
        
        {/* Month labels */}
        <div className="absolute top-0 left-0 right-0 flex justify-between text-xs text-[#CECFD2]/70 px-2">
          <span>Aug</span>
          <span>Oct</span>
          <span>Dec</span>
          <span>Feb</span>
          <span>May</span>
        </div>
        
        {/* Injury markers */}
        {seasonData.injuries.filter(injury => injury.from_date).map((injury, index) => {
          // Ensure we have valid dates
          try {
            const fromDate = new Date(injury.from_date);
            const untilDate = injury.until_date ? new Date(injury.until_date) : new Date();
            
            // Skip invalid dates
            if (isNaN(fromDate.getTime()) || isNaN(untilDate.getTime())) {
              return null;
            }
            
            // Calculate position and width based on dates
            const startPosition = Math.max(0, (fromDate.getTime() - start.getTime()) / seasonDuration * 100);
            const endPosition = Math.min(100, (untilDate.getTime() - start.getTime()) / seasonDuration * 100);
            const width = Math.max(0.5, endPosition - startPosition); // Ensure minimum width for visibility
          
          return (
            <div 
              key={index}
              className="absolute h-8 bottom-0 bg-red-500 opacity-70 rounded"
              style={{ 
                left: `${startPosition}%`, 
                width: `${width}%` 
              }}
              title={`${injury.injury || 'Unknown injury'}: ${fromDate.toLocaleDateString()} - ${untilDate.toLocaleDateString()}`}
            />
          );
        } catch (error) {
          // Skip this injury if there's an error processing it
          console.error('Error processing injury:', error, injury);
          return null;
        }
        })}
        
        </div>
      
      {/* Injury details */}
      {seasonData.injuries.length > 0 && (
        <div className="mt-3 text-sm bg-[#121212] p-3 rounded-lg shadow-lg border border-gray-500">
          <h5 className="font-bold mb-2 text-[#CECFD2]">Injuries:</h5>
          <ul className="space-y-2">
            {seasonData.injuries.map((injury, index) => (
              <li key={index} className="flex items-center gap-2 hover:bg-[#26222E] p-1 rounded-md transition-colors">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-[#CECFD2] font-medium">{injury.injury || 'Unknown injury'}</span>
                <span className="text-[#CECFD2]/70">
                  ({injury.days || 0} days)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};